import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Trophy, Flame, Users, MapPin, ChevronRight, ShoppingBag, Percent, Tag } from "lucide-react";
import { endpoints } from "@/lib/api";
import { api } from "@/lib/api";
import { LOGO, MERCH } from "@/lib/assets";
// LOGO retained in assets for future use

import StayTuned from "@/components/StayTuned";
import { toast } from "sonner";

const benefits = [
  { icon: Flame, title: "Perth's Best Builds", text: "Static custom car displays from Perth's finest JDM, euro and drift builds all under one roof." },
  { icon: Trophy, title: "People's Choice Voting", text: "Vote live from the app for your favourite cars and watch the leaderboard move in real time." },
  { icon: Users, title: "A Full Day Out", text: "Live entertainment, dyno battles, food trucks and family friendly fun. Something for everyone." },
];

export default function Home() {
  const [event, setEvent] = useState(null);
  const [salons, setSalons] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [cars, setCars] = useState([]);

  useEffect(() => {
    endpoints.event().then(setEvent).catch(() => {});
    endpoints.miniSalons().then(setSalons).catch(() => {});
    endpoints.sponsors().then(setSponsors).catch(() => {});
    endpoints.cars().then((c) => setCars(c.slice(0, 4))).catch(() => {});
  }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative h-[100svh] min-h-[600px] w-full overflow-hidden" data-testid="hero">
        {event?.hero_video && (
          <video autoPlay muted loop playsInline poster={event?.hero_poster}
            className="absolute inset-0 w-full h-full object-cover">
            <source src={event.hero_video} type="video/mp4" />
          </video>
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-[#0f0f11]" />
        <div className="absolute inset-0 grain" />
        <div className="relative z-10 h-full max-w-7xl mx-auto px-6 flex flex-col justify-end pb-16 sm:pb-24">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <div className="inline-flex items-center gap-2 border border-[#ff7a1a]/50 bg-black/40 px-4 py-1.5 mb-6">
              <span className="w-2 h-2 rounded-full bg-[#ff7a1a] animate-pulse" />
              <span className="font-heading uppercase tracking-widest text-xs text-white">2027 · Dates to be announced · Perth</span>
            </div>
            <h1 className="pas-wordmark text-6xl sm:text-8xl lg:text-[10rem] leading-[0.78]">
              <span className="pas-gradient-text">PERTH</span><br />
              <span className="text-white block">AUTO SALON</span>
            </h1>
            <p className="mt-5 max-w-xl text-white/85 text-lg sm:text-xl font-body">
              The premiere JDM and custom car culture show is back. Tokyo Auto Salon energy, Perth born.
            </p>
            <div className="flex flex-wrap gap-4 mt-8">
              <a href="#waitlist" className="pas-btn text-base" data-testid="hero-waitlist-cta">Join the Waitlist <ArrowRight size={18} /></a>
              <Link to="/exhibit" className="pas-btn-outline text-base" data-testid="hero-exhibit-cta">Exhibit Your Ride</Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="max-w-7xl mx-auto px-6 py-20 sm:py-28">
        <div className="grid md:grid-cols-3 gap-6">
          {benefits.map((b, i) => (
            <motion.div key={b.title} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: i * 0.12 }}
              className="card-hover border border-white/10 bg-[#121212] p-8" data-testid={`benefit-${i}`}>
              <div className="w-14 h-14 grid place-items-center pas-gradient mb-5" style={{ clipPath: "polygon(8px 0,100% 0,calc(100% - 8px) 100%,0 100%)" }}>
                <b.icon className="text-black" size={26} />
              </div>
              <h3 className="font-heading text-2xl font-bold text-white uppercase">{b.title}</h3>
              <p className="mt-3 text-[#8a8d91] font-body">{b.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* MINI SALON STRIP */}
      <section className="max-w-7xl mx-auto px-6 pb-8">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="font-heading uppercase tracking-[0.25em] text-[#ff7a1a] text-xs mb-2">On the road to 2027</div>
            <h2 className="font-display text-4xl sm:text-6xl uppercase text-white">Mini Salons</h2>
          </div>
          <Link to="/mini-salons" className="hidden sm:inline-flex items-center gap-1 font-heading uppercase text-sm text-white/70 hover:text-[#ff7a1a]">
            See all <ChevronRight size={16} />
          </Link>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {salons.map((s, i) => (
            <Link to={`/mini-salons/${s.slug}`} key={s.id} className="group relative overflow-hidden border border-white/10 card-hover" data-testid={`salon-card-${i}`}>
              <div className="aspect-[4/3] overflow-hidden">
                <img src={s.image} alt={s.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
              <div className="absolute bottom-0 p-5">
                <div className="text-[#ffd600] font-heading text-xs uppercase tracking-wide">{new Date(s.date).toLocaleDateString("en-AU", { month: "short", year: "numeric" })}</div>
                <h3 className="font-heading text-2xl font-bold text-white uppercase leading-tight">{s.name}</h3>
                <div className="text-white/60 text-sm mt-1 flex items-center gap-1"><MapPin size={13} /> {s.venue}</div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* MAIN EVENT TEASER + COUNTDOWN */}
      {event && (
        <section className="relative my-20 sm:my-28 py-20 overflow-hidden grain" data-testid="main-event-teaser">
          <img src={event.hero_poster} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0f0f11] via-[#0f0f11]/80 to-transparent" />
          <div className="relative max-w-7xl mx-auto px-6">
            <div className="font-heading uppercase tracking-[0.25em] text-[#b026ff] text-sm mb-3">The Main Event</div>
            <h2 className="font-display text-5xl sm:text-7xl uppercase text-white leading-[0.85] max-w-2xl">
              Two days. One legendary weekend.
            </h2>
            <p className="mt-4 max-w-xl text-[#8a8d91] font-body">{event.venue}, Perth. Lock in your spot on the waitlist and be first to hear when dates drop.</p>
            <div className="mt-8"><StayTuned /></div>
            <Link to="/main-event" className="pas-btn mt-8 text-base">Explore the Main Event <ArrowRight size={18} /></Link>
          </div>
        </section>
      )}

      {/* FEATURE CARS */}
      <section className="max-w-7xl mx-auto px-6 pb-8">
        <div className="flex items-end justify-between mb-8">
          <h2 className="font-display text-4xl sm:text-6xl uppercase text-white">Feature Builds</h2>
          <Link to="/app/cars" className="hidden sm:inline-flex items-center gap-1 font-heading uppercase text-sm text-white/70 hover:text-[#ff7a1a]">
            View all in app <ChevronRight size={16} />
          </Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {cars.map((c, i) => (
            <div key={c.id} className="group relative overflow-hidden border border-white/10 card-hover">
              <div className="aspect-[4/3] overflow-hidden">
                <img src={c.photo_url} alt={`${c.make} ${c.model}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 to-transparent" />
              <div className="absolute bottom-0 p-4">
                <div className="text-[#ff7a1a] font-heading text-xs uppercase">{c.year}</div>
                <div className="font-heading font-bold text-white uppercase leading-tight">{c.make} {c.model}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* SPONSOR WALL */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <h3 className="text-center font-heading uppercase tracking-[0.25em] text-[#8a8d91] text-sm mb-8">Backed by the best in the game</h3>
        <div className="relative overflow-hidden">
          <div className="flex gap-4 marquee w-max">
            {[...sponsors, ...sponsors].map((s, i) => (
              <div key={i} className="shrink-0 px-8 py-6 border border-white/10 bg-[#121212] min-w-[180px] grid place-items-center">
                <span className="font-display text-2xl text-white/80 uppercase whitespace-nowrap">{s.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MERCH */}
      <section className="max-w-7xl mx-auto px-6 pb-8" data-testid="merch-section">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="font-heading uppercase tracking-[0.25em] text-[#b026ff] text-xs mb-2 flex items-center gap-2"><ShoppingBag size={14} /> Gear up</div>
            <h2 className="font-display text-4xl sm:text-6xl uppercase text-white">Official Merch</h2>
          </div>
          <span className="hidden sm:inline-flex items-center gap-1 font-heading uppercase text-xs text-[#ffd600] border border-[#ffd600]/40 px-3 py-1.5">
            <Percent size={13} /> Waitlist members save 10%
          </span>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {MERCH.map((m, i) => (
            <div key={m.id} className="group border border-white/10 bg-[#121212] overflow-hidden card-hover" data-testid={`merch-${m.id}`}>
              <div className="aspect-square overflow-hidden bg-[#0a0a0c]">
                <img src={m.img} alt={m.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="p-4">
                <div className="text-[#8a8d91] font-heading text-[10px] uppercase flex items-center gap-1"><Tag size={10} /> {m.tag}</div>
                <div className="font-heading text-white uppercase leading-tight">{m.name}</div>
                <div className="flex items-center justify-between mt-2">
                  <span className="font-display text-lg pas-gradient-text uppercase whitespace-nowrap">{m.price}</span>
                  <a href="#waitlist" className="font-heading uppercase text-[10px] text-[#b026ff] border border-[#b026ff]/40 px-2 py-1 hover:bg-[#b026ff]/10">Notify me</a>
                </div>
              </div>
            </div>
          ))}
        </div>
        <p className="text-center text-[#8a8d91] font-body text-sm mt-6">Hoodies, tees, caps, key tags and more. The full store drops with first ticket release.</p>
      </section>

      {/* WAITLIST CTA */}
      <WaitlistForm />
    </div>
  );
}

function WaitlistForm() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", interest: "attendee" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) return toast.error("Name and email are required");
    setLoading(true);
    try {
      await api.post("/waitlist", form);
      setDone(true);
      toast.success("You're on the list. See you in Perth.");
    } catch (err) {
      toast.error("Something went wrong, try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="waitlist" className="relative py-20 sm:py-28 bg-[#0a0a0c] border-y border-white/10" data-testid="waitlist-section">
      <div className="max-w-5xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <div className="font-heading uppercase tracking-[0.25em] text-[#ff7a1a] text-sm mb-3">Don't miss the drop</div>
          <h2 className="font-display text-5xl sm:text-7xl uppercase text-white leading-[0.85]">Join the waitlist</h2>
          <p className="mt-4 text-[#8a8d91] font-body">Be first to know when tickets drop, get exhibitor spots and mini salon dates straight to your inbox.</p>
          <div className="mt-6 space-y-2">
            <div className="flex items-center gap-3 text-white/90 font-body" data-testid="perk-tickets">
              <span className="w-8 h-8 shrink-0 pas-gradient grid place-items-center text-black"><Percent size={16} /></span>
              <span><b className="pas-gradient-text font-heading">25% off</b> first release ticket sales</span>
            </div>
            <div className="flex items-center gap-3 text-white/90 font-body" data-testid="perk-merch">
              <span className="w-8 h-8 shrink-0 pas-gradient grid place-items-center text-black"><ShoppingBag size={16} /></span>
              <span><b className="pas-gradient-text font-heading">10% off</b> all official merchandise</span>
            </div>
          </div>
        </div>
        {done ? (
          <div className="border border-[#ff7a1a]/40 bg-[#121212] p-10 text-center" data-testid="waitlist-success">
            <Trophy className="mx-auto text-[#ffd600] mb-4" size={40} />
            <h3 className="font-heading text-2xl text-white uppercase">You're locked in</h3>
            <p className="text-[#8a8d91] mt-2">We'll be in touch soon. Get that build ready.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4 border border-white/10 bg-[#121212] p-6 sm:p-8" data-testid="waitlist-form">
            <input data-testid="waitlist-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Full name" className="w-full bg-[#0f0f11] border border-white/15 px-4 py-3 text-white outline-none focus:border-[#b026ff]" />
            <input data-testid="waitlist-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="Email address" className="w-full bg-[#0f0f11] border border-white/15 px-4 py-3 text-white outline-none focus:border-[#b026ff]" />
            <input data-testid="waitlist-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder="Phone (optional)" className="w-full bg-[#0f0f11] border border-white/15 px-4 py-3 text-white outline-none focus:border-[#b026ff]" />
            <select data-testid="waitlist-interest" value={form.interest} onChange={(e) => setForm({ ...form, interest: e.target.value })}
              className="w-full bg-[#0f0f11] border border-white/15 px-4 py-3 text-white outline-none focus:border-[#b026ff]">
              <option value="attendee">I want to attend</option>
              <option value="exhibitor">I want to exhibit a car</option>
              <option value="sponsor">I'm interested in sponsoring</option>
            </select>
            <button disabled={loading} className="pas-btn w-full text-base" data-testid="waitlist-submit-button">
              {loading ? "Sending..." : "Count me in"} <ArrowRight size={18} />
            </button>
          </form>
        )}
      </div>
    </section>
  );
}
